import { cx } from '@bangle.io/base-utils';
import {
  type EditorView,
  type HeadingItem,
  MIN_TOC_HEADINGS,
  setupScrollAndResizeHandlers,
} from '@bangle.io/prosemirror-plugins';
import {
  Button,
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  useIsMobile,
} from '@bangle.io/ui-components';
import { ListTree } from 'lucide-react';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

const ACTIVE_THRESHOLD_RATIO = 1 / 3;
/**
 * Reading column whose right margin hosts the expanded panel when there is
 * one: the app's page content, or the standalone reader's article.
 */
const PAGE_CONTENT_SELECTOR = '.B-app-page-content, .sn-reader-article';
/** Collapsed rail hit-area width used for right-margin placement. */
const RAIL_WIDTH_PX = 28;
const OPEN_DELAY_MS = 150;
const CLOSE_DELAY_MS = 200;

function prefersReducedMotion(): boolean {
  return (
    typeof window !== 'undefined' &&
    // `useIsMobile` treats a missing `matchMedia` the same way (jsdom, older
    // embedded webviews): fall back to the motion-preferred default.
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches
  );
}

function headingDom(view: EditorView, pos: number): HTMLElement | null {
  try {
    const result = view.domAtPos(pos + 1);
    const node = result.node;
    if (node instanceof HTMLElement) {
      return node.closest('h1,h2,h3,h4,h5,h6');
    }
    if (node.parentElement) {
      return node.parentElement.closest('h1,h2,h3,h4,h5,h6');
    }
  } catch {
    // Position may be stale mid-edit.
  }
  return null;
}

function scrollToHeading(view: EditorView, item: HeadingItem) {
  const el = headingDom(view, item.pos);
  if (!el) {
    return;
  }
  el.scrollIntoView({
    behavior: prefersReducedMotion() ? 'auto' : 'smooth',
    block: 'start',
  });
}

function findActiveHeadingIndex(
  view: EditorView,
  headings: HeadingItem[],
): number {
  const threshold = window.innerHeight * ACTIVE_THRESHOLD_RATIO;
  let active = 0;
  for (let i = 0; i < headings.length; i++) {
    const item = headings[i];
    if (!item) {
      continue;
    }
    const el = headingDom(view, item.pos);
    if (!el) {
      continue;
    }
    const rect = el.getBoundingClientRect();
    // Collapsed (display:none) headings report zero-size rects.
    if (rect.width === 0 && rect.height === 0) {
      continue;
    }
    if (rect.top <= threshold) {
      active = i;
    } else {
      break;
    }
  }
  return active;
}

function dashMetrics(level: number): { widthPx: number; insetPx: number } {
  const clamped = Math.min(6, Math.max(1, level));
  return {
    widthPx: Math.max(6, 16 - (clamped - 1) * 3),
    insetPx: (clamped - 1) * 3,
  };
}

function TocEntryList({
  headings,
  activeIndex,
  onJump,
  idPrefix,
}: {
  headings: HeadingItem[];
  activeIndex: number;
  onJump: (item: HeadingItem) => void;
  idPrefix: string;
}) {
  return (
    <ul className="flex flex-col gap-0.5 py-1">
      {headings.map((item, index) => {
        const active = index === activeIndex;
        return (
          <li key={`${idPrefix}-${item.pos}-${item.text}`}>
            <button
              type="button"
              title={item.text}
              aria-current={active ? 'true' : undefined}
              aria-label={t.app.editor.toc.jumpTo({ heading: item.text })}
              onMouseDown={(event) => event.preventDefault()}
              onClick={() => onJump(item)}
              className={cx(
                'block w-full truncate rounded-md px-2 py-1.5 text-left text-sm transition-colors',
                active
                  ? 'bg-accent/15 font-medium text-foreground'
                  : 'text-muted-foreground hover:bg-muted/60 hover:text-foreground',
              )}
              style={{ paddingLeft: `${0.5 + (item.level - 1) * 0.55}rem` }}
            >
              {item.text}
            </button>
          </li>
        );
      })}
    </ul>
  );
}

function TocDashRail({
  headings,
  activeIndex,
}: {
  headings: HeadingItem[];
  activeIndex: number;
}) {
  return (
    <div
      aria-hidden="true"
      className="flex flex-col items-end gap-1.5 px-1.5 py-2"
      data-testid="heading-toc-dashes"
    >
      {headings.map((item, index) => {
        const { widthPx, insetPx } = dashMetrics(item.level);
        const active = index === activeIndex;
        return (
          <span
            key={`dash-${item.pos}-${item.text}`}
            data-toc-dash=""
            data-active={active ? 'true' : undefined}
            className={cx(
              'block h-0.5 rounded-full transition-colors',
              active ? 'bg-foreground' : 'bg-muted-foreground/45',
            )}
            style={{
              width: widthPx,
              marginRight: insetPx,
            }}
          />
        );
      })}
    </div>
  );
}

export type TocRailProps = {
  /** View whose outline is listed. Renders nothing until it is available. */
  editorView: EditorView | null | undefined;
  /** Outline entries; the rail stays hidden below `MIN_TOC_HEADINGS`. */
  headings: HeadingItem[] | undefined;
  /**
   * Runs just before the scroll on a jump. The note editor uses it to expand
   * collapsed ancestors; a read-only document has nothing to expand, so it
   * omits the hook and jumps directly.
   */
  onJump?: (item: HeadingItem, editorView: EditorView) => void;
};

/**
 * Heading TOC: hover-expandable dash rail on desktop, Contents sheet on
 * mobile. Deliberately service-free — it works for both the editable note
 * editor (via `HeadingTocRail`) and the standalone reader page, which mounts
 * outside the app's core services.
 */
export function TocRail({ editorView, headings, onJump }: TocRailProps) {
  const isMobile = useIsMobile();
  const visibleHeadings = useMemo(() => {
    if (!headings || headings.length < MIN_TOC_HEADINGS) {
      return null;
    }
    return headings;
  }, [headings]);

  const [activeIndex, setActiveIndex] = useState(0);
  const [sheetOpen, setSheetOpen] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [panelRight, setPanelRight] = useState(12);
  const openTimerRef = useRef<number | null>(null);
  const closeTimerRef = useRef<number | null>(null);
  const railRef = useRef<HTMLElement | null>(null);

  const clearExpandTimers = useCallback(() => {
    if (openTimerRef.current != null) {
      window.clearTimeout(openTimerRef.current);
      openTimerRef.current = null;
    }
    if (closeTimerRef.current != null) {
      window.clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
  }, []);

  const scheduleExpand = useCallback(() => {
    clearExpandTimers();
    openTimerRef.current = window.setTimeout(() => {
      openTimerRef.current = null;
      setExpanded(true);
    }, OPEN_DELAY_MS);
  }, [clearExpandTimers]);

  const scheduleCollapse = useCallback(() => {
    clearExpandTimers();
    closeTimerRef.current = window.setTimeout(() => {
      closeTimerRef.current = null;
      setExpanded(false);
    }, CLOSE_DELAY_MS);
  }, [clearExpandTimers]);

  const expandNow = useCallback(() => {
    clearExpandTimers();
    setExpanded(true);
  }, [clearExpandTimers]);

  const collapseNow = useCallback(() => {
    clearExpandTimers();
    setExpanded(false);
  }, [clearExpandTimers]);

  useEffect(() => {
    return () => clearExpandTimers();
  }, [clearExpandTimers]);

  const updateActive = useCallback(() => {
    if (!editorView || !visibleHeadings) {
      return;
    }
    setActiveIndex(findActiveHeadingIndex(editorView, visibleHeadings));
  }, [editorView, visibleHeadings]);

  const updatePanelPosition = useCallback(() => {
    const content = document.querySelector(PAGE_CONTENT_SELECTOR);
    if (!(content instanceof HTMLElement)) {
      setPanelRight(12);
      return;
    }
    const rect = content.getBoundingClientRect();
    // Sit in the margin to the right of the reading column when there is room;
    // otherwise hug the viewport edge.
    const margin = Math.max(0, window.innerWidth - rect.right);
    setPanelRight(Math.max(8, margin - RAIL_WIDTH_PX - 8));
  }, []);

  useEffect(() => {
    if (!editorView || !visibleHeadings) {
      return;
    }
    const abort = new AbortController();
    const refresh = () => {
      updateActive();
      updatePanelPosition();
    };
    refresh();
    setupScrollAndResizeHandlers(refresh, abort.signal);
    return () => abort.abort();
  }, [editorView, visibleHeadings, updateActive, updatePanelPosition]);

  const handleJump = useCallback(
    (item: HeadingItem) => {
      if (!editorView) {
        return;
      }
      onJump?.(item, editorView);
      scrollToHeading(editorView, item);
      setSheetOpen(false);
      collapseNow();
    },
    [collapseNow, editorView, onJump],
  );

  if (!editorView || !visibleHeadings) {
    return null;
  }

  if (isMobile) {
    return (
      <div
        className="B-editor-toc-rail pointer-events-none fixed top-[calc(var(--bangle-app-titlebar-height)+0.75rem)] right-3 z-20"
        data-testid="heading-toc-rail"
      >
        <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
          <Button
            type="button"
            size="sm"
            variant="secondary"
            className="pointer-events-auto gap-1.5 shadow-md"
            aria-label={t.app.editor.toc.open}
            onMouseDown={(event) => event.preventDefault()}
            onClick={() => setSheetOpen(true)}
          >
            <ListTree className="size-4" aria-hidden />
            {t.app.editor.toc.open}
          </Button>
          <SheetContent side="bottom" className="max-h-[70vh] gap-0 pb-6">
            <SheetHeader className="pb-2">
              <SheetTitle>{t.app.editor.toc.title}</SheetTitle>
            </SheetHeader>
            <nav
              aria-label={t.app.editor.toc.label}
              className="min-h-0 flex-1 overflow-y-auto px-2"
            >
              <TocEntryList
                headings={visibleHeadings}
                activeIndex={activeIndex}
                onJump={handleJump}
                idPrefix="toc-mobile"
              />
            </nav>
          </SheetContent>
        </Sheet>
      </div>
    );
  }

  return (
    <nav
      ref={railRef}
      aria-label={t.app.editor.toc.label}
      data-testid="heading-toc-rail"
      data-toc-expanded={expanded ? 'true' : 'false'}
      className={cx(
        'B-editor-toc-rail group fixed top-1/2 z-20 -translate-y-1/2',
        'opacity-70 transition-opacity focus-within:opacity-100 hover:opacity-100',
        prefersReducedMotion() && 'transition-none',
      )}
      style={{ right: panelRight }}
      onPointerEnter={scheduleExpand}
      onPointerLeave={scheduleCollapse}
      onBlur={(event) => {
        const next = event.relatedTarget;
        if (next instanceof Node && event.currentTarget.contains(next)) {
          return;
        }
        scheduleCollapse();
      }}
      onKeyDown={(event) => {
        if (event.key === 'Escape') {
          event.stopPropagation();
          collapseNow();
          const active = document.activeElement;
          if (
            active instanceof HTMLElement &&
            railRef.current?.contains(active)
          ) {
            active.blur();
          }
        }
      }}
    >
      <button
        type="button"
        tabIndex={expanded ? -1 : 0}
        aria-expanded={expanded}
        aria-label={t.app.editor.toc.label}
        className={cx(
          'pointer-events-auto block rounded-md outline-none focus-visible:ring-2 focus-visible:ring-ring',
          expanded && 'invisible',
        )}
        onMouseDown={(event) => event.preventDefault()}
        onFocus={expandNow}
        onClick={expandNow}
      >
        <TocDashRail headings={visibleHeadings} activeIndex={activeIndex} />
      </button>

      {expanded ? (
        <div
          className="absolute top-1/2 right-0 max-h-[min(24rem,70vh)] w-52 -translate-y-1/2 overflow-y-auto rounded-lg border border-border/50 bg-background/95 p-1 shadow-sm ring-1 ring-foreground/5 backdrop-blur-sm"
          data-testid="heading-toc-panel"
        >
          <p className="px-2 pt-1.5 pb-1 font-medium text-muted-foreground text-xs">
            {t.app.editor.toc.title}
          </p>
          <TocEntryList
            headings={visibleHeadings}
            activeIndex={activeIndex}
            onJump={handleJump}
            idPrefix="toc-desktop"
          />
        </div>
      ) : null}
    </nav>
  );
}
